/**
 * AccountStatusGuard — wraps protected routes. Renders a full-screen
 * notice when the signed-in user is banned, and a top banner + page-level
 * gating when suspended. Active users see the children unchanged.
 *
 * The guard never logs the user out — admins can reinstate accounts
 * without forcing a fresh signup. Banned users get a "Contact Support"
 * link that deep-links to the support page.
 */
import { useNavigate } from "react-router-dom";
import { ShieldAlert, MessageCircle, Clock, LifeBuoy, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    ACCOUNT_STATUS,
    getEffectiveStatus,
} from "@/lib/moderationStore";

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch { return null; }
};

const FullScreenNotice = ({ tone, icon: Icon, title, body, reason, until, onSupport, onSignOut }) => (
    <div
        data-testid="account-status-notice"
        className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm px-5"
    >
        <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-card">
            <div className="flex flex-col items-center text-center">
                <span
                    className={[
                        "h-14 w-14 rounded-2xl flex items-center justify-center mb-3",
                        tone === "ban"
                            ? "bg-destructive/10 text-destructive"
                            : "bg-amber-500/10 text-amber-700 dark:text-amber-300",
                    ].join(" ")}
                >
                    <Icon size={28} />
                </span>
                <h1 className="font-display text-[20px] font-semibold tracking-tight">
                    {title}
                </h1>
                <p className="mt-2 text-[13.5px] text-muted-foreground max-w-[300px]">
                    {body}
                </p>
                {reason && (
                    <div
                        data-testid="account-status-reason"
                        className="mt-4 w-full rounded-xl bg-secondary border border-border px-3 py-2 text-left"
                    >
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium">
                            Reason
                        </div>
                        <div className="text-[13px] mt-0.5">{reason}</div>
                    </div>
                )}
                {until && (
                    <div
                        data-testid="account-status-until"
                        className="mt-2 w-full rounded-xl bg-secondary border border-border px-3 py-2 text-left inline-flex items-center gap-2"
                    >
                        <Clock size={14} className="text-muted-foreground" />
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium">
                                Suspension ends
                            </div>
                            <div className="text-[13px] mt-0.5">{formatDate(until) || until}</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-5 space-y-2">
                <button
                    type="button"
                    data-testid="account-status-support"
                    onClick={onSupport}
                    className="w-full h-12 rounded-2xl bg-primary text-primary-foreground text-[14px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                    <LifeBuoy size={15} />
                    Contact Support
                </button>
                <button
                    type="button"
                    data-testid="account-status-signout"
                    onClick={onSignOut}
                    className="w-full h-11 rounded-2xl bg-secondary border border-border text-[12.5px] font-medium text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-2"
                >
                    <LogOut size={13} />
                    Sign out
                </button>
            </div>
        </div>
    </div>
);

const AccountStatusGuard = ({ children }) => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();

    if (!user) return children;

    const status = getEffectiveStatus(user);

    if (status === ACCOUNT_STATUS.BANNED) {
        return (
            <FullScreenNotice
                tone="ban"
                icon={ShieldAlert}
                title="Your account has been banned"
                body="Please contact support if you believe this is a mistake."
                reason={user.ban_reason}
                until={null}
                onSupport={() => navigate("/support")}
                onSignOut={signOut}
            />
        );
    }

    // Suspended users still get to navigate to /support, but everywhere
    // else they hit the same notice (which keeps gating consistent).
    if (status === ACCOUNT_STATUS.SUSPENDED) {
        // Allow the support route through so users can request help.
        if (typeof window !== "undefined" && window.location.pathname.startsWith("/support")) {
            return (
                <>
                    <SuspendedBanner reason={user.suspension_reason} until={user.suspended_until} />
                    {children}
                </>
            );
        }
        return (
            <FullScreenNotice
                tone="suspend"
                icon={MessageCircle}
                title="Your account is temporarily suspended"
                body="Posting, booking, payments and messaging are paused while your account is suspended. You can still contact support."
                reason={user.suspension_reason}
                until={user.suspended_until}
                onSupport={() => navigate("/support")}
                onSignOut={signOut}
            />
        );
    }

    return children;
};

const SuspendedBanner = ({ reason, until }) => (
    <div
        data-testid="suspended-banner"
        className="px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/30 text-amber-700 dark:text-amber-300 text-[12px]"
    >
        <strong className="font-semibold">Account suspended.</strong>{" "}
        {reason ? <>{reason}.{" "}</> : null}
        {until ? <>Ends {formatDate(until) || until}. </> : null}
        Limited access while suspended.
    </div>
);

export default AccountStatusGuard;
